/**
 * A school's own colour, made safe to put on a button and the active navigation
 * (`docs/product/admin-web-prd.md` §6). A school can pick any colour; WCAG 2.2 AA cannot be
 * optional, so the text on it, its hover and the ink used on cards are derived, never assumed.
 */
export interface BrandColours {
  /** The primary action's fill. */
  action: string;
  actionHover: string;
  /** Text on `action` and `actionHover`: white or ink, whichever reads at 4.5:1. */
  onAction: string;
  /** The colour as text or an icon on a light card, at 4.5:1. */
  inkLight: string;
  /** The same on a dark card. */
  inkDark: string;
}

const WHITE = '#ffffff';
const INK = '#14110f';
const CARD_LIGHT = '#ffffff';
const CARD_DARK = '#1e1a18';
const AA = 4.5;

type Rgb = [number, number, number];

const parse = (hex: string): Rgb => [
  Number.parseInt(hex.slice(1, 3), 16),
  Number.parseInt(hex.slice(3, 5), 16),
  Number.parseInt(hex.slice(5, 7), 16),
];

const format = ([r, g, b]: Rgb): string =>
  `#${[r, g, b].map((v) => Math.round(v).toString(16).padStart(2, '0')).join('')}`;

const mix = (hex: string, toward: string, amount: number): string => {
  const a = parse(hex);
  const b = parse(toward);
  return format([0, 1, 2].map((i) => (a[i] ?? 0) + ((b[i] ?? 0) - (a[i] ?? 0)) * amount) as Rgb);
};

function luminance(hex: string): number {
  const [r, g, b] = parse(hex).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  }) as Rgb;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** The WCAG contrast ratio of two `#rrggbb` colours. */
export function contrast(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

/** Moves `hex` toward `toward` in small steps until it reads against `against`. */
function untilReadable(hex: string, toward: string, against: string): string {
  let colour = hex;
  for (let step = 0; step < 20 && contrast(colour, against) < AA; step++) {
    colour = mix(colour, toward, 0.1);
  }
  return colour;
}

export function brandColours(primary: unknown): BrandColours | null {
  if (typeof primary !== 'string' || !/^#[0-9a-f]{6}$/i.test(primary)) return null;
  const base = primary.toLowerCase();

  let action = base;
  let onAction = WHITE;
  if (contrast(base, WHITE) < AA) {
    if (contrast(base, INK) >= AA) onAction = INK;
    else action = untilReadable(base, INK, WHITE);
  }

  // Hover moves away from the text colour, so its contrast only grows.
  const actionHover =
    onAction === WHITE
      ? mix(action, INK, 0.15)
      : untilReadable(mix(action, WHITE, 0.2), WHITE, INK);

  return {
    action,
    actionHover,
    onAction,
    inkLight: untilReadable(base, INK, CARD_LIGHT),
    inkDark: untilReadable(base, WHITE, CARD_DARK),
  };
}
